import { Input } from "@components";
import { DotsVertical, Trash01 } from "@untitledui/icons";
import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/base/buttons/button";
import { QuestionType } from "@/types/question";

type QuestionOption = {
	id: string;
	text: string;
	icon?: React.ComponentType<{ className?: string }>;
};

type OptionsEditorProps = {
	options: QuestionOption[];
	type: QuestionTypeOrChecklist;
	onUpdate: (id: string, text: string) => void;
	onDelete: (id: string) => void;
	onReorder?: (fromIndex: number, toIndex: number) => void;
};

type QuestionTypeOrChecklist = QuestionType | 999;

type OptionDragState = {
	optionId: string;
	fromIndex: number;
	startY: number;
};

export const OptionsEditor = ({
	options,
	type,
	onUpdate,
	onDelete,
	onReorder,
}: OptionsEditorProps) => {
	const [dragState, setDragState] = useState<OptionDragState | null>(null);
	const [isDragging, setIsDragging] = useState(false);
	const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
	const dragOverIndexRef = useRef<number | null>(null);
	const isDraggingRef = useRef(false);
	const onReorderRef = useRef(onReorder);

	onReorderRef.current = onReorder;

	const canReorder =
		type === QuestionType.Option && typeof onReorder === "function";

	useEffect(() => {
		if (!dragState) {
			return;
		}

		const handleMouseMove = (event: MouseEvent) => {
			if (Math.abs(event.clientY - dragState.startY) > 5) {
				isDraggingRef.current = true;
				setIsDragging(true);
			}
		};

		const handleMouseUp = () => {
			const overIndex = dragOverIndexRef.current;
			if (
				isDraggingRef.current &&
				overIndex !== null &&
				overIndex !== dragState.fromIndex
			) {
				onReorderRef.current?.(dragState.fromIndex, overIndex);
			}
			dragOverIndexRef.current = null;
			isDraggingRef.current = false;
			setDragState(null);
			setIsDragging(false);
			setDragOverIndex(null);
		};

		window.addEventListener("mousemove", handleMouseMove);
		window.addEventListener("mouseup", handleMouseUp);
		return () => {
			window.removeEventListener("mousemove", handleMouseMove);
			window.removeEventListener("mouseup", handleMouseUp);
		};
	}, [dragState]);

	if (type === 999) {
		return (
			<div className="grid grid-cols-3 gap-4">
				{options.map((option) => {
					const OptionIcon = option.icon;
					return (
						<div
							className="flex flex-col items-center justify-center rounded-lg border border-gray-200 bg-white p-4 transition-colors hover:bg-gray-50"
							key={option.id}
						>
							<div className="mb-2">
								{OptionIcon ? (
									<OptionIcon className="h-6 w-6 text-gray-600" />
								) : null}
							</div>
							<span className="font-medium text-gray-700 text-sm">
								{option.text}
							</span>
						</div>
					);
				})}
			</div>
		);
	}

	return (
		<ul className="space-y-3">
			{options.map((option, index) => {
				const isBeingDragged =
					isDragging === true && dragState?.optionId === option.id;

				return (
					// biome-ignore lint/a11y/noNoninteractiveElementInteractions: drop target during option drag
					<li
						className="relative list-none"
						key={option.id}
						onMouseEnter={() => {
							if (dragState !== null && dragState.optionId !== option.id) {
								dragOverIndexRef.current = index;
								setDragOverIndex(index);
							}
						}}
					>
						{dragOverIndex === index &&
						isDragging &&
						dragState?.optionId !== option.id ? (
							<div className="mb-1 h-0.5 w-full rounded bg-blue-500" />
						) : null}
						<div
							className={`flex items-center gap-3 ${isBeingDragged ? "opacity-40" : ""}`}
						>
							{type === QuestionType.Option ? (
								<button
									aria-label={`Arrastar opção ${index + 1}`}
									className={`cursor-grab touch-none rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 active:cursor-grabbing ${canReorder ? "" : "pointer-events-none"}`}
									onMouseDown={(event) => {
										if (!canReorder || event.button !== 0) {
											return;
										}
										event.preventDefault();
										setDragState({
											optionId: option.id,
											fromIndex: index,
											startY: event.clientY,
										});
										isDraggingRef.current = false;
										setIsDragging(false);
									}}
									tabIndex={0}
									type="button"
								>
									<DotsVertical aria-hidden className="h-4 w-4" />
								</button>
							) : null}
							<Input
								className="flex-1"
								disabled={type === QuestionType.Boolean}
								maxLength={100}
								onChange={(e) => {
									if (type === QuestionType.Option) {
										const value = e.target.value;
										if (value.length <= 100) {
											onUpdate(option.id, value);
										}
									}
								}}
								placeholder={`Opção ${index + 1}`}
								value={option.text}
							/>
							{type === QuestionType.Option ? (
								<Button
									color="tertiary"
									onClick={() => onDelete(option.id)}
									size="sm"
								>
									<Trash01 className="h-4 w-4" />
								</Button>
							) : null}
						</div>
					</li>
				);
			})}
		</ul>
	);
};
